import { FC, useMemo } from 'react';
import { generatePath } from 'react-router-dom';

import { Card } from '@components/card';
import { isV2Form } from '@components/form-builder';
import { IconButton } from '@components/icon-button';
import { IconForm } from '@components/icons/form';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { IconReplace } from '@components/icons/replace';
import { Menu } from '@components/menu';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { toQueryString } from '@utils/requests';

import { useForm, useFormTemplates } from 'api/forms/hooks';
import { FormResponseV2, FormTemplateBase } from 'api/forms/requests';
import { useCurrentWorkspace } from 'ui/authenticated/main/helpers';
import { routePaths } from 'ui/authenticated/navigation';

import { FormPreviewButton, IsTemplateProp } from './template-preview';

export const InstantiatedFormCard = ({ actionId }: { actionId: string }) => {
  const formQuery = useForm(actionId);

  if (!formQuery.isSuccess) return null;
  if (!isV2Form(formQuery.data.fieldsConfig)) return null;
  const form = formQuery.data as FormResponseV2;

  return <FormDisplayCard name={form.name} description={form.description} shortId={actionId} />;
};

export const TemplateCard = ({
  templateId,
  onReplace,
  onlyAllowPreview = false,
}: {
  templateId?: string;
  onReplace?(): void;
  onlyAllowPreview?: boolean;
}) => {
  const { t } = useScopedTranslate(locale);
  const { workspaceId } = useCurrentWorkspace();
  const queryString = useMemo(() => {
    return toQueryString({
      workspace: workspaceId,
    });
  }, [workspaceId]);
  const templatesQuery = useFormTemplates(queryString);
  const selectedTemplate = templatesQuery.data?.find((template) => template.shortId === templateId);
  const builderPath = templateId
    ? generatePath(routePaths.workspaceFormTemplatesEdit, {
        workspaceId,
        shortId: templateId,
        '*': '',
      })
    : undefined;

  return (
    <>
      <FormDisplayCard
        name={selectedTemplate?.name ?? ''}
        description={selectedTemplate?.description ?? ''}
        shortId={templateId ?? ''}
        isTemplate
      >
        {!onlyAllowPreview && onReplace && (
          <Menu
            id="menuid"
            trigger={IconButton}
            triggerProps={{ icon: <IconMoreVertical />, variant: 'ghost-purple' }}
          >
            {builderPath && (
              <a href={builderPath} target="_blank" rel="noopener noreferrer">
                <Menu.Item icon={<IconOpenNewTab />} label={t('open')} />
              </a>
            )}
            {onReplace && (
              <Menu.Item icon={<IconReplace />} label={t('replace')} onSelect={onReplace} />
            )}
          </Menu>
        )}
      </FormDisplayCard>
    </>
  );
};

type FormDisplayCardProps = FormTemplateBase & IsTemplateProp;

const FormDisplayCard: FC<FormDisplayCardProps> = ({ name, children, description, ...rest }) => {
  return (
    <Card spaceY={2} spaceX={4}>
      <Stack align="center" space={3} orientation="horizontal">
        <IconForm />
        <div style={{ flexGrow: 1 }}>
          <Text font="semibold14" text={name} />
          <Text font="regular14" color="textSecondary" text={description} />
        </div>
        <Stack orientation="horizontal">
          <FormPreviewButton {...rest} />
          {children}
        </Stack>
      </Stack>
    </Card>
  );
};

const locale = {
  en: {
    preview: 'Preview template',
    open: 'Open template',
    replace: 'Replace template',
  },
};
